import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/tube";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useConfig } from "@/lib/config";
import { mutationResponse } from "@/utils/trpc";
import type { TubeDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof createSchema>;

export function TubeDetailForm({ tube }: { tube?: TubeDetail }) {
  const { data: fieldSets } = trpc.admin.sampleFieldSet.list.useQuery();
  const { data: specimenTypes } = trpc.admin.tube.listSpecimenTypes.useQuery();
  const create = trpc.admin.tube.create.useMutation();
  const update = trpc.admin.tube.update.useMutation();
  const config = useConfig();

  const navigate = useNavigate();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: tube?.name ?? "",
      fieldSetId: (tube?.fieldSetId ?? null) as number,
      specimenTypeId: tube?.specimenTypeId ?? "tube",
      requireCentrifuge: tube?.requireCentrifuge ?? false,
    },
    resolver: zodResolver(createSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = (value) => {
    if (tube) {
      update.mutate(
        { id: tube.id, ...value },
        mutationResponse({
          errorTitle: "Error updating container",
          successMessage: "Container updated",
        }),
      );
    } else {
      create.mutate(
        value,
        mutationResponse({
          errorTitle: "Error adding container",
          successMessage: "Container added",
          onSuccess(data) {
            navigate({
              to: "/admin/tubes/$id/edit",
              params: { id: data.id },
            });
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Name</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="fieldSetId"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Select
                {...field}
                selectedKey={field.value}
                onSelectionChange={(value) => {
                  field.onChange(value as number);
                }}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Field set</Label>
                <SelectTrigger />
                <SelectList items={fieldSets || []}>
                  {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
                </SelectList>
                <FieldError>{error?.message}</FieldError>
              </Select>
            )}
          />
          {!config.enableSubjectSampleMigration && (
            <Controller
              control={control}
              name="specimenTypeId"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Select
                  {...field}
                  selectedKey={field.value}
                  onSelectionChange={(value) => {
                    field.onChange(value as string);
                  }}
                  isInvalid={!!error}
                  validationBehavior="aria"
                  isDisabled
                >
                  <Label>Specimen type</Label>
                  <SelectTrigger />
                  <SelectList items={specimenTypes || []}>
                    {(item) => (
                      <SelectItem id={item.value}>
                        {item.description}
                      </SelectItem>
                    )}
                  </SelectList>
                  <FieldError>{error?.message}</FieldError>
                </Select>
              )}
            />
          )}
        </div>
        <Separator />
        <div className="flex justify-end gap-2">
          <Link to="/admin/tubes" variant="outline" asButton>
            Cancel
          </Link>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
          >
            Save
          </Button>
        </div>
      </AdminForm>
    </TabLayout>
  );
}
